import type { LocationQuery } from "vue-router";

const DEFAULT_PAGE_SIZE = "12";

/**
 * Upper bound for the single request that restores a load-more list on a
 * `?p` deep link, refresh or Back — the listing endpoint is used with a
 * `pageSize` of at most 100.
 */
export const MAX_PRELOADED_ITEMS = 100;

export interface ListingPage {
	/** `pageNo` for the initial product request. */
	pageNo: string;
	/** `pageSize` for the initial product request. */
	pageSize: string;
	/** Page the listing is on once the initial items are shown. */
	currentPage: number;
	/** Size of every later page (pagination links, "show more"). */
	itemsPerPage: string;
}

/**
 * Initial request for a product listing (category, manufacturer).
 *
 * Numbered pagination fetches page `p`; the shopper's page-size cookie wins
 * over Storyblok. "Show more" fetches pages 1..p in one request so the list
 * a `?p` URL points at is whole, and ignores the cookie because that mode has
 * no page-size control to change it.
 */
export const resolveListingPage = ({
	p,
	storyblokPageSize,
	cookiePageSize,
	loadMore
}: {
	p: unknown;
	storyblokPageSize?: string | number | null;
	cookiePageSize?: string | number | null;
	loadMore: boolean;
}): ListingPage => {
	const requestedPage = Math.max(1, parseInt(String(p), 10) || 1);
	const storyblokSize = storyblokPageSize
		? String(storyblokPageSize)
		: DEFAULT_PAGE_SIZE;

	if (!loadMore) {
		const size = cookiePageSize ? String(cookiePageSize) : storyblokSize;
		return {
			pageNo: String(requestedPage),
			pageSize: size,
			currentPage: requestedPage,
			itemsPerPage: size
		};
	}

	const size = parseInt(storyblokSize, 10) || parseInt(DEFAULT_PAGE_SIZE, 10);
	const maxPages = Math.max(1, Math.floor(MAX_PRELOADED_ITEMS / size));
	const pages = Math.min(requestedPage, maxPages);
	return {
		pageNo: "1",
		pageSize: String(pages * size),
		currentPage: pages,
		itemsPerPage: String(size)
	};
};

/** Query for page `page`, keeping filter/sort; page 1 carries no `p`. */
export const pageQuery = (
	query: LocationQuery | Record<string, string>,
	page: number
): Record<string, string> => {
	const next = { ...(query as Record<string, string>) };
	if (page > 1) next.p = String(page);
	else delete next.p;
	return next;
};
